import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormArray,
  AbstractControl,
} from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { NumberRangeValidator } from 'src/app/shared/validators/number-range.validator';
import { AttributesEditorDataSource } from './attributesEditorDataSource';
import { EditableAttribute } from 'src/app/shared/models/attributes/editableAttribute';

import * as _ from 'lodash';
import { AttributeDefinitionCondition } from 'src/app/shared/models/attributes/attributeDefinitionCondition';
import {
  MultiselectEvent,
  MultiselectItem,
} from 'src/app/shared/components/multiselect/multiselect.component';
import { CustomDocumentUploadFile } from '../custom-document-uploader/custom-document-upload-file';
import { AttributeEditorMode } from '../../models/attributes/attributeEditorMode';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ComplexFieldEditorComponent } from '../complex-field-editor/complex-field-editor.component';
import { ComplexAttributeWrite } from '../../models/attributes/complexAttributeWrite';
import {
  ComplexAttributeItems,
  FlatComplexAttributeValue,
} from '../../models/attributes/flatAttribute';
import { ComplexAttributeSubFieldValue } from '../../models/attributes/complexAttributeSubFieldValue';
import { ModalConfirmationComponent } from '../modal-confirmation/modal-confirmation.component';
import { ApiVersions } from '../../services/api-version';
import { DateRangeValidator } from '../../validators/date-range.validator';
import { ApplicationFormPage } from '../../models/application/formPage';
import { AttributeService } from 'src/app/shared/services/attribute.service';

@Component({
  selector: 'attributes-editor',
  templateUrl: 'attributes-editor.component.html',
  styleUrls: ['attributes-editor.component.scss'],
})
export class AttributesEditorComponent {
  private _dataSource: AttributesEditorDataSource = new AttributesEditorDataSource();
  private _dataSourceInitialized: boolean = false;
  @Input() mode: AttributeEditorMode = AttributeEditorMode.Application;

  @Input()
  set dataSource(value: AttributesEditorDataSource) {
    this._dataSource = value;
    if (this._dataSource) {
      this._dataSource.editableAttributes.subscribe((attributes) => {
        if (attributes && !this._dataSourceInitialized) {
          this._dataSource.editable
            .filter((x) => x.conditions && x.conditions.length > 0)
            .forEach((attribute) => {
              this.isVisible(attribute);
            });
          this.setupPage();
          this.setupEditAttributesForm();
          this._dataSourceInitialized = true;
        }
      });
    }
  }
  @Output() blurred = new EventEmitter<boolean>();
  get dataSource(): AttributesEditorDataSource {
    return this._dataSource;
  }

  @Input() fxFlexWidth: string = '100%';
  @Input() useAttributeDescription: boolean = false;
  @Input() onTeamForm: boolean = false;
  @Input() secureLinkId: string = '';
  @Input() isUploading: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() entityId = '';
  @Input() formPage: ApplicationFormPage;

  @Output() fieldUpdated: EventEmitter<string> = new EventEmitter<string>();
  @Output() downloadFile: EventEmitter<{ fileName: string; attributeDefinitionId: string }> =
    new EventEmitter<{ fileName: string; attributeDefinitionId: string }>();

  @Output() addOrUpdateComplexValue: EventEmitter<{
    attributeDefinitionId: string;
    writeModel: ComplexAttributeWrite[];
  }> = new EventEmitter<{ attributeDefinitionId: string; writeModel: ComplexAttributeWrite[] }>();

  attributesForm: UntypedFormGroup;
  editAttributes = [];

  get isValid(): boolean {
    return this.attributesForm && this.attributesForm.valid;
  }

  get personDetailsMode(): boolean {
    return this.mode == AttributeEditorMode.PersonDetails;
  }

  get teamDetailsMode(): boolean {
    return this.mode == AttributeEditorMode.TeamDetails;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private attributeService: AttributeService
  ) {}

  isProfileEditorMode(): boolean {
    return this.mode == AttributeEditorMode.PersonDetails
      || this.mode == AttributeEditorMode.TeamDetails;
  }

  assignValueToAttribute(attribute: EditableAttribute) {
    if (
      attribute.isDateType() &&
      this.attributesForm.get(attribute.attributeDefinitionId).errors &&
      this.attributesForm.get(attribute.attributeDefinitionId).hasError('matDatepickerParse')
    ) {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors({
        invaliddate: attribute.name + ' must be a valid date in mm/dd/yyyy format.',
      });
      attribute.setOrAddValue(EditableAttribute.INVALID_DATE_VALUE);
      return;
    }

    let originalValue =
      typeof attribute.value === undefined ||
      attribute.value === EditableAttribute.INVALID_DATE_VALUE
        ? null
        : attribute.value;

    let currentValue = this.attributesForm.get(attribute.attributeDefinitionId).value;

    if (
      attribute.isEncryptedFieldType() &&
      (currentValue == '***' || currentValue == attribute.decryptedValue)
    )
      return;

    let attributeChanged: boolean = false;

    if (
      (originalValue === undefined || originalValue === null) &&
      (currentValue === undefined || currentValue === null)
    ) {
      attributeChanged = false;
    } else {
      attributeChanged = originalValue !== currentValue;
    }

    attribute.edited = attribute.edited || attributeChanged;

    if (attributeChanged) {
      attribute.setOrAddValue(currentValue);
      if (attribute.isDropDownType()) {
        this.updateQuestionsVisibility(attribute);
      }
    }
    this.blurred.emit(attributeChanged);
  }

  togglePasswordVisibility(attribute: any): void {
    this.attributeService
      .decryptAttribute(attribute.attributeDefinitionId, attribute.value)
      .subscribe((result) => {
        if (result) {
          this.attributesForm.get(attribute.attributeDefinitionId).setValue(result);
          attribute.decryptedValue = result;
          this.attributesForm.get(attribute.attributeDefinitionId).enable();
        }
      });
  }

  private setupPage() {
    if (this.formPage) {
      this.formPage.editableAttributes = [];
      let attrIds = this._dataSource.attributeDefinitions
        .filter((x) => x.formPageId == this.formPage.id)
        .map((y) => y.id);
      this._dataSource.editable?.forEach((attr) => {
        if (attrIds.includes(attr.attributeDefinitionId)) {
          this.formPage.editableAttributes.push(attr);
        }
      });
    } else {
      this.formPage = new ApplicationFormPage();
      this.formPage.editableAttributes = this._dataSource.editable;
    }
  }

  private setupEditAttributesForm() {
    const form = {};

    this._dataSource.editable.forEach((attribute: Attribute) => {
      let validators = [];
      let value = undefined;
      if (attribute.required) {
        validators.push(NotEmptyValidator(`${attribute.name} is required`));
      }
      if (attribute.isNumberType()) {
        let attributeDefinition = this.getAttributeDefinitionById(attribute.attributeDefinitionId);
        validators.push(
          NumberRangeValidator(
            parseInt(attributeDefinition.min),
            parseInt(attributeDefinition.max),
            `${attribute.name}`
          )
        );
      }

      if (attribute.isDateRangeType()) {
        validators.push(DateRangeValidator(`${attribute.value}`, `${attribute.name}`));
      }

      if (attribute.isCheckboxType()) {
        value = this.getCheckboxes(attribute);
      } else {
        value = attribute.getValue();
      }
      attribute.defaultValue = attribute.getValue();
      if (attribute.type == 'EncryptedField' && value) {
        form[attribute.attributeDefinitionId] = [{ value: '***', disabled: this.isProfileEditorMode() }, validators];
      } else {
        form[attribute.attributeDefinitionId] = [value, validators];
      }
    });

    this.attributesForm = this.formBuilder.group(form);
  }

  getAttributeDefinitionById(id): AttributeDefinition {
    return _.find(
      this._dataSource.attributeDefinitions,
      (attributeDefinition) => attributeDefinition.id === id
    );
  }

  addComplexRangeError(attribute) {
    if (
      this.mode == AttributeEditorMode.Application &&
      attribute._min &&
      attribute.complexAttributeValues.length < Number(attribute._min)
    ) {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors({
        complexRange: attribute.name + ' must have at least ' + attribute._min + ' entries.',
      });
    } else {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors(null);
    }
  }
  attributeComplex(attribute: EditableAttribute): boolean {
    if (this.attributesForm && this.attributesForm.controls[attribute.attributeDefinitionId]) {
      this.addComplexRangeError(attribute);
    }
    return attribute._max
      ? attribute.complexAttributeValues.length >= Number(attribute._max)
      : false;
  }

  getCheckboxes(attribute: Attribute): UntypedFormArray {
    const comp = this;
    let checkboxControls = comp.formBuilder.array([]);
    const checkboxAttributeDefinition = _.find(
      this._dataSource.attributeDefinitions,
      (attrDef: AttributeDefinition) => attrDef.id == attribute.attributeDefinitionId
    );
    const selectedValues = _.flatten(
      _.filter(
        this._dataSource.editable,
        (attr: Attribute) => attr.attributeDefinitionId == attribute.attributeDefinitionId
      ).map((attr) => attr.valueList)
    );

    _.forEach(
      checkboxAttributeDefinition.valueList,
      (valueListItem: AttributeDefinitionValueListItem) => {
        const selectedAttribute = _.find(selectedValues, (v) => v.id == valueListItem.id);

        let item = {
          id: valueListItem.id,
          checked: selectedAttribute ? true : false,
        };

        checkboxControls.push(comp.formBuilder.control(item));
      }
    );

    return checkboxControls;
  }

  onInputBlurred() {
    this.blurred.emit();
  }

  markAllAsTouched() {
    this._dataSource.editable.forEach((attr) => {
      if (attr.isVisible) {
        if (attr.isCheckboxType()) {
          this.attributesForm.get(attr.attributeDefinitionId).value.touched = true;
        } else {
          this.attributesForm.get(attr.attributeDefinitionId).markAsTouched();
        }
      }
    });
  }

  nonValidAttributes(): Map<string, AbstractControl> {
    let nonValidAttributes: Map<string, AbstractControl> = new Map<string, AbstractControl>();
    if (this._dataSource.editable) {
      this._dataSource.editable.forEach((attr) => {
        if (attr.isVisible && !attr.isValid) {
          nonValidAttributes.set(
            attr.attributeDefinitionId,
            this.attributesForm.get(attr.attributeDefinitionId)
          );
        }
      });
    }
    return nonValidAttributes;
  }

  nonValidAttributesinPage(page: ApplicationFormPage): Map<string, AbstractControl> {
    let nonValidAttributes: Map<string, AbstractControl> = new Map<string, AbstractControl>();
    if (page.editableAttributes) {
      page.editableAttributes.forEach((attr) => {
        if (attr.isVisible && !attr.isValid) {
          nonValidAttributes.set(
            attr.attributeDefinitionId,
            this.attributesForm.get(attr.attributeDefinitionId)
          );
        }
      });
    }
    return nonValidAttributes;
  }

  isAttributeTouched(attr) {
    return this.attributesForm.get(attr.attributeDefinitionId).touched;
  }

  longAnswerChanged(attribute: Attribute, text: string) {
    attribute.value = text;
    attribute.edited = true;
    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);

    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
  }

  dateRangeChanged(attribute: Attribute, text: any) {
    attribute.value = text;
    attribute.edited = true;
    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);

    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
  }

  skillChanged(attribute: Attribute, selectedSkill: AttributeDefinitionValueListItem) {
    attribute.edited = true;
    attribute.attributeDefinitionValueListItem = selectedSkill;

    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);
    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
    this.updateQuestionsVisibility(attribute);
  }

  customDocumentUploaderChanged(attribute: Attribute, uploadedFiles: CustomDocumentUploadFile[]) {
    attribute.edited = true;

    let value = uploadedFiles
      .map((x) => x.fileName.trim())
      .join('|')
      .toString();
    attribute.value = value ?? null;
    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);
    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
    this.blurred.emit(true);
  }

  multiSelectChanged(attribute: Attribute, event: MultiselectEvent) {
    attribute.edited = true;

    const attrDefinition = this.dataSource.attributeDefinitions.find(
      (x) => x.id == attribute.attributeDefinitionId
    );
    const selectedItems = event.selectedItems.map((x) =>
      attrDefinition.valueList.find((a) => a.id == x.id)
    );
    attribute.valueList = selectedItems;

    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);
    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
    this.updateQuestionsVisibility(attribute);
  }

  updateCheckboxAttributeDefinition(
    attribute: Attribute,
    valueListItem: AttributeDefinitionValueListItem,
    event: MatCheckboxChange
  ) {
    let editableAttribute = _.find(this._dataSource.editable, function (personAttribute) {
      return attribute.attributeDefinitionId == personAttribute.attributeDefinitionId;
    });

    attribute.edited = true;
    editableAttribute.edited = true;

    if (event.checked) {
      editableAttribute.setOrAddValue(valueListItem);
    } else {
      editableAttribute.removeValue(valueListItem.itemValue);
    }
    this.updateQuestionsVisibility(attribute);

    if (!editableAttribute.required) {
      return;
    }

    const attributeInForm = <UntypedFormArray>(
      this.attributesForm.get(attribute.attributeDefinitionId)
    );
    const collection = attributeInForm.value.controls;
    let valueListItemInForm = _.find(collection, (control) => control.value.id == valueListItem.id);

    valueListItemInForm.value.checked = event.checked;
    valueListItemInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
  }

  getCheckboxStatus(attribute: Attribute, valueListItem: AttributeDefinitionValueListItem) {
    const personAttribute = _.find(this._dataSource.editable, function (personAttribute) {
      return personAttribute.attributeDefinitionId == attribute.attributeDefinitionId;
    });

    const checked = _.find(
      personAttribute.valueList,
      function (currentValueListItem: AttributeDefinitionValueListItem) {
        return currentValueListItem.id == valueListItem.id;
      }
    );

    return checked != undefined;
  }

  getAttributeValueList(attribute: Attribute): AttributeDefinitionValueListItem[] {
    let type = _.find(
      this._dataSource.attributeDefinitions,
      (attrDef) => attrDef.id === attribute.attributeDefinitionId
    );
    return type.valueList;
  }
  private cachedMultiselectValue: Record<string, MultiselectItem[]> = {};

  getMultiselectValueList(attribute: Attribute): MultiselectItem[] {
    const type = this._dataSource.attributeDefinitions.find(
      (attrDef) => attrDef.id === attribute.attributeDefinitionId
    );
    //need to cache the dropdown for the attribute because the MultiselectComponent.items setter has logic we should try and not trigger
    if (
      this.cachedMultiselectValue[attribute.attributeDefinitionId]?.length != type.valueList.length
    ) {
      //do a deep comparison instead?
      this.cachedMultiselectValue[attribute.attributeDefinitionId] = type.valueList.map(
        (v) =>
          new MultiselectItem(
            v.id,
            v.itemValue,
            attribute.valueList.findIndex((a) => a.id == v.id) > -1
          )
      );
    }
    return this.cachedMultiselectValue[attribute.attributeDefinitionId];
  }

  get shortAnswerAttributeUiType(): AttributeUiType {
    return AttributeUiType.ShortAnswer;
  }

  get encryptedFieldAttributeUiType(): AttributeUiType {
    return AttributeUiType.EncryptedField;
  }

  get numberAttributeUiType(): AttributeUiType {
    return AttributeUiType.Number;
  }

  get dateAttributeUiType(): AttributeUiType {
    return AttributeUiType.Date;
  }

  get longAnswerAttributeUiType(): AttributeUiType {
    return AttributeUiType.LongAnswer;
  }

  get dropDownAttributeUiType(): AttributeUiType {
    return AttributeUiType.DropDown;
  }

  get checkboxAttributeUiType(): AttributeUiType {
    return AttributeUiType.Checkboxes;
  }

  get multiSelectAttributeUiType(): AttributeUiType {
    return AttributeUiType.MultiSelect;
  }

  get skillAttributeUiType(): AttributeUiType {
    return AttributeUiType.Skill;
  }

  get starRatingAttributeUiType(): AttributeUiType {
    return AttributeUiType.StarRating;
  }

  get documentUiType(): AttributeUiType {
    return AttributeUiType.Document;
  }

  get dateRangeUiType(): AttributeUiType {
    return AttributeUiType.DateRange;
  }

  get complexUiType(): AttributeUiType {
    return AttributeUiType.Complex;
  }

  onAddComplexValue(attribute: EditableAttribute) {
    let dialogRef = this.dialog.open(ComplexFieldEditorComponent, {
      data: { attribute: attribute, attributes: [], attributeEditorMode: this.mode },
    });

    dialogRef.afterClosed().subscribe((result: AttributesEditorDataSource) => {
      if (result) {
        attribute.edited = true;

        let complexFieldWrite = result.asComplexAttributeWrite(attribute);
        this.addOrUpdateComplexAttributeValue(attribute, complexFieldWrite, result.editable);
        if (this.mode != AttributeEditorMode.Application) {
          this.dataSource.editable[
            this.dataSource.editable.findIndex(
              (x) => x.attributeDefinitionId == attribute.attributeDefinitionId
            )
          ] = attribute;
          this.addOrUpdateComplexValue.emit({
            attributeDefinitionId: attribute.attributeDefinitionId,
            writeModel: this.dataSource.asComplexAttributeWrites()[0],
          });
        }
      }
    });
  }

  onEditComplexValue(
    attribute: EditableAttribute,
    complexAttributeValue: FlatComplexAttributeValue
  ) {
    let editItemIndex = attribute.complexAttributeValues.indexOf(complexAttributeValue);
    var attrs: Attribute[] = [];
    complexAttributeValue.items.forEach((x) => {
      x.subFieldValues.forEach((y) => {
        let attr = Attribute.parse({
          value: y.value,
          type: y.type,
          attributeDefinitionValueListItem: y.attributeDefinitionValueListItem,
          attributeDefinitionId: attribute.subFieldList.find(
            (sub) => sub.id == y.subFieldDefinitionItemId
          ).subFieldDefinition.id,
        });
        attrs.push(attr);
      });
    });
    let dialogRef = this.dialog.open(ComplexFieldEditorComponent, {
      data: { attribute: attribute, attributes: attrs, attributeEditorMode: this.mode },
    });

    dialogRef.afterClosed().subscribe((result: AttributesEditorDataSource) => {
      if (result) {
        attribute.edited = true;

        let complexFieldWrite = result.asComplexAttributeWrite(attribute);
        this.addOrUpdateComplexAttributeValue(
          attribute,
          complexFieldWrite,
          result.editable,
          editItemIndex
        );
        if (this.mode != AttributeEditorMode.Application) {
          this.dataSource.editable[
            this.dataSource.editable.findIndex(
              (x) => x.attributeDefinitionId == attribute.attributeDefinitionId
            )
          ] = attribute;
          this.addOrUpdateComplexValue.emit({
            attributeDefinitionId: attribute.attributeDefinitionId,
            writeModel: this.dataSource.asComplexAttributeWrites()[0],
          });
        }
      }
    });
  }

  onDeleteComplexValue(
    attribute: EditableAttribute,
    complexAttributeValue: FlatComplexAttributeValue
  ) {
    let dialogRef = this.dialog.open(ModalConfirmationComponent, {
      data: { action: `delete ${attribute.name}`, delete: true },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        attribute.edited = true;

        _.remove(attribute.complexAttributeValues, complexAttributeValue);
        this.addComplexRangeError(attribute);
        if (this.mode != AttributeEditorMode.Application) {
          this.dataSource.editable[
            this.dataSource.editable.findIndex(
              (x) => x.attributeDefinitionId == attribute.attributeDefinitionId
            )
          ] = attribute;
          this.addOrUpdateComplexValue.emit({
            attributeDefinitionId: attribute.attributeDefinitionId,
            writeModel: this.dataSource.asComplexAttributeWrites()[0],
          });
        }
      }
    });
  }

  dropDownAttributeDefinitionValueListItemComparer(
    option: AttributeDefinitionValueListItem,
    value: AttributeDefinitionValueListItem
  ): boolean {
    return option && value ? option.id == value.id : false;
  }

  isVisible(attribute: EditableAttribute) {
    const initVisibility = attribute.isVisible;
    if (attribute.hasCondition) {
      const checkResult = this.areConditionsMet(attribute.conditions);
      checkResult ? attribute.show() : this.resetValues(attribute);
    }
    if (initVisibility != attribute.isVisible) {
      this.onAttributeVisbilityChanged(attribute);
    }
    return attribute.isVisible;
  }

  isDisplayed(text: string): boolean {
    if (!!text && text.length > 110) {
      return true;
    }

    return false;
  }
  onDownloadFile(fileName: string, attribute: Attribute) {
    this.downloadFile.emit({
      fileName: fileName,
      attributeDefinitionId: attribute.attributeDefinitionId,
    });
  }

  private addOrUpdateComplexAttributeValue(
    attribute: EditableAttribute,
    complexFieldWrite: ComplexAttributeWrite,
    definations: EditableAttribute[],
    editeItemIndex: number = -1
  ) {
    let groupId =
      attribute.complexAttributeValues.length > 0
        ? attribute.complexAttributeValues[attribute.complexAttributeValues.length - 1].groupId + 1
        : 1;
    let flatAtt = new FlatComplexAttributeValue();

    flatAtt.groupId = groupId;

    flatAtt.items = complexFieldWrite.complexSubFieldValues.map((v, index) => {
      let subFieldValues = [];
      let type = attribute.subFieldList.find(
        (x) => x.subFieldDefinitionId == v.subFieldItemValue.attributeDefinitionId
      ).subFieldDefinition.uiType;

      let name = attribute.subFieldList.find(
        (x) => x.subFieldDefinitionId == v.subFieldItemValue.attributeDefinitionId
      ).subFieldDefinition.name;
      let rank = index + 1;
      var attr = definations.find(
        (a) => a.attributeDefinitionId == v.subFieldItemValue.attributeDefinitionId
      );
      if (type == AttributeUiType.MultiSelect || type == AttributeUiType.Checkboxes) {
        attr.valueList.forEach((vl) => {
          let subFieldValue = new ComplexAttributeSubFieldValue(
            v.subFieldDefinitionItemId,
            groupId,
            vl.itemValue,
            name,
            type,
            vl.rank,
            vl
          );
          subFieldValues.push(subFieldValue);
        });
      } else {
        let subFieldValue = new ComplexAttributeSubFieldValue(
          v.subFieldDefinitionItemId,
          groupId,
          attr.attributeDefinitionValueListItem
            ? attr.attributeDefinitionValueListItem.itemValue
            : attr.value,
          name,
          type,
          rank,
          attr.attributeDefinitionValueListItem
        );
        subFieldValues = [subFieldValue];
      }
      let item = new ComplexAttributeItems(
        v.subFieldDefinitionItemId,
        subFieldValues,
        name,
        type,
        rank
      );

      return item;
    });
    if (editeItemIndex == -1) {
      attribute.complexAttributeValues.push(flatAtt);
    } else {
      attribute.complexAttributeValues[editeItemIndex] = flatAtt;
    }
    this.addComplexRangeError(attribute);
  }
  private areConditionsMet(conditions: AttributeDefinitionCondition[]): boolean {
    let result = false;
    conditions.forEach((condition) => {
      let targetQuestion = this.dataSource.editable.find(
        (x) => x.attributeDefinitionId == condition.targetAttributeDefinitionId
      );
      if (!targetQuestion.isVisible) {
        result = false;
      } else if (_.intersection(condition.conditionValues, targetQuestion.values()).length > 0) {
        result = true;
      }
    });
    return result;
  }

  private updateQuestionsVisibility(attribute: Attribute) {
    this._dataSource.editable
      .filter((x) =>
        x.conditions?.find((x) => x.targetAttributeDefinitionId == attribute.attributeDefinitionId)
      )
      .forEach((attribute) => {
        this.isVisible(attribute);
        this.updateQuestionsVisibility(attribute);
      });
  }
  private resetValues(attribute: EditableAttribute) {
    attribute.hide();
    if (!this.attributesForm) return;
    if (attribute.allowsMultipleValues) {
      attribute.valueList = attribute.defaultValue
        ? [...(<AttributeDefinitionValueListItem[]>attribute.defaultValue)]
        : [];
      this.attributesForm.controls[attribute.attributeDefinitionId].setValue(
        attribute.isCheckboxType() ? this.getCheckboxes(attribute) : attribute.valueList
      );
    } else {
      this.attributesForm.controls[attribute.attributeDefinitionId].setValue(
        attribute.defaultValue
      );
      this.assignValueToAttribute(attribute);
    }

    if (attribute.isCheckboxType()) {
      this.attributesForm.get(attribute.attributeDefinitionId).value.touched = false;
    }
    this.attributesForm.get(attribute.attributeDefinitionId).markAsUntouched();
  }

  public getUploadPath(attribute: Attribute) {
    if (this.personDetailsMode) {
      return {
        path: `user/${this.entityId}/custom-fields/${attribute.attributeDefinitionId}/custom-document`,
        apiVersion: ApiVersions.v1_0,
      };
    }

    if (this.teamDetailsMode) {
      return {
        path: `teams/${this.entityId}/${attribute.attributeDefinitionId}/custom-document`,
        apiVersion: ApiVersions.v0_1,
      };
    }

    return {
      path: `documents/${this.secureLinkId}/${attribute.attributeDefinitionId}/scan-upload-temporary`,
      apiVersion: ApiVersions.v0_1,
    };
  }

  onFieldChanged(attributeDefinitionId: string) {
    this.fieldUpdated.emit(attributeDefinitionId);
  }

  onAttributeVisbilityChanged(attribute: EditableAttribute) {
    this.onFieldChanged(attribute.attributeDefinitionId);
  }
}
